/**
 * @file pricing.util.ts
 * @path apps/website/src/features/booking/utils/pricing.util.ts
 * @description Pure pricing functions. No React, no side effects.
 */

import { Activity, CompletedBooking, Service } from "@sirbastians/types";

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

const parsePriceString = (price: string): number =>
  parseFloat(price.replace(/[R,\s]/g, "")) || 0;

/**
 * Returns the flat price for a tiered pricing option list.
 * Optionally filters by duration label ("Half Day" / "Full Day").
 */
export const getTieredPrice = (
  options: Array<{ label: string; price: string }> | undefined,
  passengerCount: number,
  duration?: "half-day" | "full-day" | null,
): number => {
  if (!options?.length) return 0;

  for (const option of options) {
    if (duration) {
      const isHalfDay = /Half\s*Day/i.test(option.label);
      const isFullDay = /Full\s*Day/i.test(option.label);
      if (duration === "half-day" && !isHalfDay) continue;
      if (duration === "full-day" && !isFullDay) continue;
    }

    const rangeMatch = option.label.match(
      /(\d+)\s*-\s*(\d+)\s*(Pax|Passengers?)/i,
    );
    if (rangeMatch) {
      const min = parseInt(rangeMatch[1], 10);
      const max = parseInt(rangeMatch[2], 10);
      if (passengerCount >= min && passengerCount <= max) {
        return parsePriceString(option.price);
      }
    }
  }

  return 0;
};

// ---------------------------------------------------------------------------
// Exported price calculators
// ---------------------------------------------------------------------------

/**
 * Calculates the base service price for a single booking draft.
 * Infants are never included in Transport tier calculations.
 */
export const getServicePrice = (
  service: Service | null,
  adultsCount: number,
  childrenCount: number,
  infantsCount: number,
  duration?: "half-day" | "full-day" | null,
): number => {
  if (!service) return 0;

  if (service.category === "Transport") {
    const totalPassengers = adultsCount + childrenCount;

    if (service.slug === "private-vehicle-hire" && duration) {
      return getTieredPrice(
        service.pricing?.options,
        totalPassengers,
        duration,
      );
    }

    return getTieredPrice(service.pricing?.options, totalPassengers);
  }

  if (service.category === "Tours" && service.packageConfig?.passengerConfig) {
    const config = service.packageConfig.passengerConfig;
    const adultsToCharge = Math.max(adultsCount, config.minAdults);
    let total = adultsToCharge * config.adultPrice;
    if (childrenCount > 0) total += childrenCount * config.childPrice;
    if (infantsCount > 0) total += infantsCount * config.infantPrice;
    return total;
  }

  const totalPassengers = adultsCount + childrenCount;
  return (service.pricing?.amount ?? 0) * totalPassengers;
};

/**
 * Calculates the total optional-activities price.
 * Included activities are excluded from the charge.
 * Charged per paying passenger (adults + children).
 */
export const getActivitiesPrice = (
  selectedActivities: Activity[],
  includedActivities: Activity[],
  payingPassengers: number,
): number => {
  return selectedActivities.reduce((sum, activity) => {
    const isIncluded = includedActivities.some((inc) => inc.id === activity.id);
    if (isIncluded) return sum;
    return sum + (activity.pricing?.amount ?? 0) * payingPassengers;
  }, 0);
};

/**
 * Baby seats × R350, trailer = R450.
 */
export const getAddOnsPrice = (
  babySeats: number,
  trailerNeeded: boolean,
): number => {
  let total = 0;
  if (babySeats > 0) total += babySeats * 350;
  if (trailerNeeded) total += 450;
  return total;
};

/**
 * Extra stops × service.packageConfig.extraStopPrice.
 */
export const getExtraStopsPrice = (
  routeStops: Array<{ isExtraStop: boolean }>,
  service: Service | null,
): number => {
  const extraStopCount = routeStops.filter((s) => s.isExtraStop).length;
  if (extraStopCount === 0 || !service?.packageConfig?.extraStopPrice) return 0;
  return extraStopCount * service.packageConfig.extraStopPrice;
};

/**
 * VIP vehicles only — find option by id and multiply price × count.
 */
export const getVehiclesPrice = (
  selectedVehicles: Array<{
    id: string;
    type: "standard" | "vip";
    count: number;
  }>,
  service: Service | null,
): number => {
  return selectedVehicles.reduce((sum, vehicle) => {
    if (vehicle.type !== "vip" || !service?.vipOptions) return sum;
    const vipOption = service.vipOptions.find(
      (opt: { id: string; price: number }) => opt.id === vehicle.id,
    );
    return vipOption ? sum + vipOption.price * vehicle.count : sum;
  }, 0);
};

export interface PriceBreakdown {
  servicePrice: number;
  activitiesPrice: number;
  addOnsPrice: number;
  extraStopsPrice: number;
  vehiclesPrice: number;
  total: number;
}

/**
 * Full price breakdown for the current booking draft.
 */
export const getTotalPrice = (params: {
  service: Service | null;
  includedActivities: Activity[];
  selectedActivities: Activity[];
  adultsCount: number;
  childrenCount: number;
  infantsCount: number;
  babySeats: number;
  trailerNeeded: boolean;
  routeStops: Array<{ isExtraStop: boolean }>;
  selectedVehicles: Array<{
    id: string;
    type: "standard" | "vip";
    count: number;
  }>;
  duration?: "half-day" | "full-day" | null;
}): PriceBreakdown => {
  const {
    service,
    includedActivities,
    selectedActivities,
    adultsCount,
    childrenCount,
    infantsCount,
    babySeats,
    trailerNeeded,
    routeStops,
    selectedVehicles,
    duration,
  } = params;

  const payingPassengers = adultsCount + childrenCount;

  if (!service || payingPassengers === 0) {
    return {
      servicePrice: 0,
      activitiesPrice: 0,
      addOnsPrice: 0,
      extraStopsPrice: 0,
      vehiclesPrice: 0,
      total: 0,
    };
  }

  const servicePrice = getServicePrice(
    service,
    adultsCount,
    childrenCount,
    infantsCount,
    duration,
  );
  const activitiesPrice = getActivitiesPrice(
    selectedActivities,
    includedActivities,
    payingPassengers,
  );
  const addOnsPrice = getAddOnsPrice(babySeats, trailerNeeded);
  const extraStopsPrice = getExtraStopsPrice(routeStops, service);
  const vehiclesPrice = getVehiclesPrice(selectedVehicles, service);
  const total =
    servicePrice +
    activitiesPrice +
    addOnsPrice +
    extraStopsPrice +
    vehiclesPrice;

  return {
    servicePrice,
    activitiesPrice,
    addOnsPrice,
    extraStopsPrice,
    vehiclesPrice,
    total,
  };
};

/**
 * Sums the totals for all completed bookings in the cart.
 */
export const calculateTotalForAllBookings = (
  bookings: CompletedBooking[],
): number => bookings.reduce((sum, b) => sum + b.pricing.total, 0);
